import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Palette } from 'lucide-react';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const SubmitArt = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    artist_name: '',
    email: '',
    phone: '',
    art_title: '',
    art_description: '',
    art_medium: '',
    instagram_handle: '',
    portfolio_link: '',
    message: ''
  });
  const [artworkFiles, setArtworkFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      alert('Maximum 5 files allowed');
      return;
    }
    setArtworkFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (artworkFiles.length === 0) {
      alert('Please upload at least one artwork file');
      return;
    }

    try {
      setSubmitting(true);
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('artist_name', formData.artist_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('art_title', formData.art_title);
      submitData.append('art_description', formData.art_description);
      submitData.append('art_medium', formData.art_medium);
      submitData.append('instagram_handle', formData.instagram_handle || '');
      submitData.append('portfolio_link', formData.portfolio_link || '');
      submitData.append('message', formData.message || '');
      
      // Append artwork files
      artworkFiles.forEach((file, index) => {
        submitData.append('artwork_files', file);
      });

      await axios.post(`${BACKEND_URL}/api/art-submissions`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Thank you for your submission! We will review your artwork and get back to you soon.');
      
      // Reset form
      setFormData({
        artist_name: '',
        email: '',
        phone: '',
        art_title: '',
        art_description: '',
        art_medium: '',
        instagram_handle: '',
        portfolio_link: '',
        message: ''
      });
      setArtworkFiles([]);
    } catch (error) {
      console.error('Error submitting art:', error);
      alert('Failed to submit your artwork. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-purple-600 hover:text-purple-700 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg p-8 mb-8">
          <div className="flex items-center justify-center mb-4">
            <Palette className="w-12 h-12" />
          </div>
          <h1 className="text-4xl font-bold text-center mb-4">Submit Your Art</h1>
          <p className="text-center text-lg opacity-90 max-w-2xl mx-auto">
            We celebrate artists and their unique creations. Share your artwork with us and
            get a chance to be featured on our Art Tee collection. Every month, we collaborate
            with talented artists to bring wearable art to our community.
          </p>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4">What We're Looking For</h2>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start space-x-3">
              <span className="text-purple-600 font-bold">•</span>
              <p><strong>Original Artwork:</strong> We feature unique, original art that tells a story</p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-purple-600 font-bold">•</span>
              <p><strong>High-Quality Designs:</strong> Artwork should be suitable for printing on apparel</p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-purple-600 font-bold">•</span>
              <p><strong>Women Empowerment Themes:</strong> Art that celebrates strength, beauty, and community</p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-purple-600 font-bold">•</span>
              <p><strong>Artist Collaboration:</strong> We work closely with selected artists for monthly collections</p>
            </div>
          </div>
        </div>

        {/* Submission Form */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-6">Artist Submission Form</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Artist Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Artist Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="artist_name"
                  value={formData.artist_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram Handle
                </label>
                <input
                  type="text"
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="@yourhandle"
                />
              </div>
            </div>

            {/* Artwork Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Art Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="art_title"
                value={formData.art_title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="The name of your artwork"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Art Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="art_description"
                value={formData.art_description}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="Tell us about your artwork, the inspiration behind it, and what it represents"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Art Medium <span className="text-red-500">*</span>
              </label>
              <select
                name="art_medium"
                value={formData.art_medium}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              >
                <option value="">Select medium</option>
                <option value="digital">Digital Art</option>
                <option value="painting">Painting</option>
                <option value="illustration">Illustration</option>
                <option value="mixed-media">Mixed Media</option>
                <option value="photography">Photography</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Artwork File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Artwork <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition">
                <input
                  type="file"
                  id="artwork-upload"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  required
                  className="hidden"
                />
                <label
                  htmlFor="artwork-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-purple-600 mb-3" />
                  <span className="text-sm font-medium text-gray-700 mb-1">
                    Click to upload your artwork files
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, PDF (Max 5 files, 10MB each)
                  </span>
                </label>
                {artworkFiles.length > 0 && (
                  <div className="mt-4 text-left bg-gray-50 rounded p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Selected files ({artworkFiles.length}):
                    </p>
                    <ul className="space-y-1">
                      {artworkFiles.map((file, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <span className="text-purple-600 mr-2">✓</span>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Please upload high-resolution images of your artwork. You can upload multiple files.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portfolio Link
              </label>
              <input
                type="url"
                name="portfolio_link"
                value={formData.portfolio_link}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="https://yourportfolio.com or Google Drive link with artwork samples"
              />
              <p className="text-sm text-gray-500 mt-1">
                Please provide a link to your portfolio, Instagram, or a Google Drive folder with your artwork samples
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="Anything else you'd like us to know?"
              />
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-600">
                <span className="text-red-500">*</span> Required fields
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Upload className="w-5 h-5" />
                <span>{submitting ? 'Submitting...' : 'Submit Artwork'}</span>
              </button>
            </div>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-8 bg-purple-50 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-2">
            Questions about submissions?
          </p>
          <a
            href="mailto:jasjeevbhasin5757@gmail.com"
            className="text-purple-600 hover:text-purple-700 font-semibold"
          >
            jasjeevbhasin5757@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubmitArt;
